import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";

type UserRole = "super_admin" | "admin" | "manager" | "learner";

interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
}

interface OrganizationContextType {
  organization: Organization | null;
  userRole: UserRole | null;
  effectiveRole: UserRole | null;
  realIsSuperAdmin: boolean; // Always reflects true super admin status
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isLearner: boolean;
  loading: boolean;
  setOrganizationBySlug: (slug: string, forceRefresh?: boolean) => Promise<boolean>;
  clearOrganization: () => void;
  setTestRole: (role: UserRole) => void;
  clearTestRole: () => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [realIsSuperAdmin, setRealIsSuperAdmin] = useState(false);
  const [testRoleOverride, setTestRoleOverride] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const pendingLookupRef = useRef(new Map<string, Promise<boolean>>());

  // Check if user is super admin
  useEffect(() => {
    const checkSuperAdmin = async () => {
      if (!user) {
        setRealIsSuperAdmin(false);
        return;
      }

      const { data } = await supabase
        .from("super_admins")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      setRealIsSuperAdmin(!!data);
    };

    checkSuperAdmin();
  }, [user]);

  // Only restore test role override from localStorage after confirming super admin status
  useEffect(() => {
    if (realIsSuperAdmin) {
      const stored = localStorage.getItem("test_role_override");
      if (stored && ["admin", "manager", "learner"].includes(stored)) {
        setTestRoleOverride(stored as UserRole);
      }
    } else {
      // Clear any override if not a super admin
      setTestRoleOverride(null);
      localStorage.removeItem("test_role_override");
    }
  }, [realIsSuperAdmin]);

  const setOrganizationBySlug = useCallback(async (slug: string, forceRefresh = false): Promise<boolean> => {
    if (!user) return false;

    const lookupKey = `${slug}:${forceRefresh ? "force" : "cached"}`;

    if (!forceRefresh) {
      const pendingLookup = pendingLookupRef.current.get(lookupKey);
      if (pendingLookup) return pendingLookup;
    }
    
    // Skip re-fetching if the org is already loaded for this slug (unless forced)
    if (!forceRefresh && organization?.slug === slug && userRole !== null) {
      return true;
    }

    const lookupPromise = (async () => {
      setLoading(true);

      try {
        const [{ data: org, error: orgError }, { data: saData, error: saError }] = await Promise.all([
          supabase
            .from("organizations")
            .select("*")
            .eq("slug", slug)
            .maybeSingle(),
          supabase
            .from("super_admins")
            .select("id")
            .eq("user_id", user.id)
            .maybeSingle(),
        ]);

        if (orgError || !org || saError) {
          return false;
        }

        const isSA = !!saData;
        setRealIsSuperAdmin(isSA);

        if (isSA) {
          setOrganization(org);
          setUserRole("super_admin");
          return true;
        }

        const { data: membership, error: membershipError } = await supabase
          .from("organization_members")
          .select("role")
          .eq("organization_id", org.id)
          .eq("user_id", user.id)
          .maybeSingle();

        if (membershipError || !membership) {
          return false;
        }

        setOrganization(org);
        setUserRole((membership.role as UserRole) || null);
        return true;
      } catch {
        return false;
      } finally {
        pendingLookupRef.current.delete(lookupKey);
        setLoading(false);
      }
    })();

    if (!forceRefresh) {
      pendingLookupRef.current.set(lookupKey, lookupPromise);
    }

    return lookupPromise;
  }, [user, organization?.slug, userRole]);

  const clearOrganization = () => {
    setOrganization(null);
    setUserRole(null);
  };

  const setTestRole = (role: UserRole) => {
    // Only super admins can set test roles
    if (!realIsSuperAdmin) return;
    setTestRoleOverride(role);
    localStorage.setItem("test_role_override", role);
  };

  const clearTestRole = () => {
    setTestRoleOverride(null);
    localStorage.removeItem("test_role_override");
  };

  useEffect(() => {
    if (!user) {
      clearOrganization();
      setLoading(false);
    }
  }, [user]);

  // Calculate effective role (respects test override for super admins)
  const effectiveRole: UserRole | null = realIsSuperAdmin && testRoleOverride 
    ? testRoleOverride 
    : userRole;

  // Derived permissions based on effective role
  const isSuperAdmin = effectiveRole === "super_admin" || (realIsSuperAdmin && !testRoleOverride);
  const isAdmin = effectiveRole === "admin" || isSuperAdmin;
  const isManager = effectiveRole === "manager" || isAdmin;
  const isLearner = effectiveRole === "learner";

  const value: OrganizationContextType = {
    organization,
    userRole,
    effectiveRole,
    realIsSuperAdmin,
    isSuperAdmin,
    isAdmin,
    isManager,
    isLearner,
    loading,
    setOrganizationBySlug,
    clearOrganization,
    setTestRole,
    clearTestRole,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
}
